import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import {KTCard, KTCardBody} from '_metronic/helpers'

interface PreviewData {
  [key: string]: string
}

interface TableMappings {
  [table: string]: {[key: string]: string}
}

interface ForeignKeyOption {
  id: number
  name: string
  firstName?: string
  lastName?: string
  cnic?: string
  [key: string]: any
}

interface ForeignKeyOptions {
  [key: string]: ForeignKeyOption[]
}

export interface CsvImportReviewComponentProps {
  file: File | null
  preview: PreviewData[]
  tableMappings: TableMappings
  columnMappings?: {[csvColumn: string]: string}
  warnings?: string[]
  foreignKeyOptions?: ForeignKeyOptions
  defaultValues?: {[key: string]: any}
  onDefaultValuesChange?: (selectedMappings: {[key: string]: any}) => void
  /** "Records to Import" when the caller knows what the import will send; else the preview rows. */
  recordCount?: number
}

const DB_COLUMN_DISPLAY_NAMES: {[key: string]: string} = {
  'branch.branch.name': 'Branch Name',
  'branch.branch.address': 'Branch Address',
  'branch.branch.phone': 'Branch Phone',
  'class.class.name': 'Class Name',
  'grades.grades.name': 'Grade Name',
  'levels.levels.name': 'Level Name',
  'sections.sections.name': 'Section Name',
  'subject.subject.name': 'Subject Name',
  'student_subject.student_subject.studentId': 'Student ID',
  'student_subject.student_subject.subjectId': 'Subject ID',
  'user_student.user_student.firstName': 'Student First Name',
  'user_student.user_student.lastName': 'Student Last Name',
  'user_student.user_student.studentno': 'Student Registration Number',
  'user_student.user_student.cnic': 'Student CNIC',
  'user_teacher.user_teacher.firstName': 'Teacher First Name',
  'user_teacher.user_teacher.lastName': 'Teacher Last Name',
  'user_teacher.user_teacher.employeeId': 'Teacher Employee ID',
  'user_student.user_student.address': 'Student Address',
  'user_student.user_student.phone': 'Student Phone',
  'user_student.user_student.mobile': 'Student Mobile',
  'user_student.user_student.registrationNo': 'Registration No',
  'user_student.user_student.email': 'Student Email',
  'user.user.email': 'Email',
  'user.user.username': 'Username',
  'class_room.class_room.name': 'Class Room Name',
}

const formatCsvColumnName = (name: string): string => {
  return name
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}

const getDbColumnDisplayName = (dbKey: string): string => {
  if (DB_COLUMN_DISPLAY_NAMES[dbKey]) return DB_COLUMN_DISPLAY_NAMES[dbKey]
  const parts = dbKey.split('.')
  const lastPart = parts[parts.length - 1] || dbKey
  return formatCsvColumnName(lastPart)
}

const CsvImportReview: React.FC<CsvImportReviewComponentProps> = ({
  file,
  preview,
  tableMappings,
  columnMappings = {},
  warnings = [],
  foreignKeyOptions = {},
  defaultValues = {},
  onDefaultValuesChange,
  recordCount,
}) => {
  const intl = useIntl()
  const [selectedDefaults, setSelectedDefaults] = useState<{[key: string]: any}>(
    defaultValues || {}
  )
  const onDefaultValuesChangeRef = useRef(onDefaultValuesChange)

  useEffect(() => {
    onDefaultValuesChangeRef.current = onDefaultValuesChange
  }, [onDefaultValuesChange])

  useEffect(() => {
    setSelectedDefaults(defaultValues || {})
  }, [defaultValues])

  const missingColumns = useMemo(() => {
    const columns: string[] = []
    warnings.forEach((warning: string) => {
      const patterns = [
        /Required column (\w+) is missing/,
        /Missing required column[:\s]+(\w+)/,
        /Column (\w+) is required but not found/,
        /(\w+) column is missing/,
      ]

      let foundMatch = false
      for (const pattern of patterns) {
        const match = warning.match(pattern)
        if (match && match[1] && !columns.includes(match[1])) {
          columns.push(match[1])
          foundMatch = true
          break
        }
      }

      if (
        !foundMatch &&
        typeof warning === 'string' &&
        warning.trim() &&
        !columns.includes(warning)
      ) {
        if (foreignKeyOptions[warning] || /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(warning)) {
          columns.push(warning)
        }
      }
    })
    return columns
  }, [warnings, foreignKeyOptions])

  const isBranchColumnName = useCallback((name: string) => {
    const n = name?.toLowerCase?.() || ''
    return n === 'branch' || n === 'branches' || n.includes('branch')
  }, [])

  const isLevelColumnName = useCallback((name: string) => {
    const n = name?.toLowerCase?.() || ''
    return n === 'level' || n === 'levels' || n === 'grades' || n.includes('level')
  }, [])

  const getSelectedBranchId = useCallback(
    (defaults: {[key: string]: any}) => {
      const entry = Object.entries(defaults || {}).find(([key]) => isBranchColumnName(key))
      const selected = entry ? entry[1] : undefined
      return selected?.id
    },
    [isBranchColumnName]
  )

  useEffect(() => {
    if (missingColumns.length === 0) return
    if (!foreignKeyOptions || Object.keys(foreignKeyOptions).length === 0) return

    const defaults: {[key: string]: any} = {}

    const branchKey = missingColumns.find((c) => isBranchColumnName(c))
    if (branchKey) {
      const branchOptions = foreignKeyOptions[branchKey] || []
      if (branchOptions.length > 0) {
        defaults[branchKey] = branchOptions[0]
      }
    }

    const selectedBranchId = getSelectedBranchId(defaults)

    missingColumns.forEach((column) => {
      const options = foreignKeyOptions[column] || []
      if (!options.length) return

      if (isLevelColumnName(column) && selectedBranchId) {
        const filtered = options.filter(
          (opt) =>
            opt?.branch_id === selectedBranchId ||
            opt?.branchId === selectedBranchId ||
            opt?.branch === selectedBranchId ||
            opt?.branch?.id === selectedBranchId
        )
        if (filtered.length > 0) {
          defaults[column] = filtered[0]
        }
      } else if (!defaults[column]) {
        defaults[column] = options[0]
      }
    })

    setSelectedDefaults((prev) => {
      const changed = Object.keys(defaults).some((k) => !prev[k] || prev[k].id !== defaults[k]?.id)
      if (changed) return {...defaults, ...prev}
      return prev
    })
  }, [
    missingColumns,
    foreignKeyOptions,
    isBranchColumnName,
    isLevelColumnName,
    getSelectedBranchId,
  ])

  useEffect(() => {
    if (!onDefaultValuesChangeRef.current) return
    onDefaultValuesChangeRef.current(selectedDefaults)
  }, [selectedDefaults])

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div id='csv-review' className='row'>
      <div className='col-12'>
        <h4 id='csv-review-header' className='mb-5 text-gray-900 fw-bold'>
          <i className='fas fa-clipboard-check me-2 text-primary'></i>
          {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.REVIEW_AND_IMPORT'})}
        </h4>

        {/* File Information Card */}
        <div id='csv-review-info-card'>
          <KTCard className='mb-5'>
            <KTCardBody className='py-5'>
              <div className='d-flex align-items-center mb-5'>
                <div className='symbol symbol-50px me-4'>
                  <span className='symbol-label bg-light-success'>
                    <i className='fas fa-check-circle text-success fs-2x'></i>
                  </span>
                </div>
                <div className='flex-grow-1' style={{minWidth: 0}}>
                  <h4 className='fw-bold text-gray-900 mb-1'>
                    {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.READY_TO_IMPORT'})}
                  </h4>
                  <span className='text-muted fs-7 text-break'>
                    {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.FILE_NAME'})}:{' '}
                    <strong>{file?.name || 'Unknown'}</strong>
                  </span>
                </div>
              </div>

              <div className='separator separator-dashed mb-5'></div>

              <div className='row g-5'>
                <div className='col-md-4'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-45px me-4'>
                      <span className='symbol-label bg-light-primary'>
                        <i className='fas fa-database text-primary fs-3'></i>
                      </span>
                    </div>
                    <div>
                      <span className='text-muted fs-7 d-block'>
                        {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.RECORD_COUNT'})}
                      </span>
                      <span className='fw-bold text-gray-900 fs-4'>
                        {(recordCount ?? preview.length).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-45px me-4'>
                      <span className='symbol-label bg-light-warning'>
                        <i className='fas fa-table text-warning fs-3'></i>
                      </span>
                    </div>
                    <div>
                      <span className='text-muted fs-7 d-block'>
                        {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.TABLE_COUNT'})}
                      </span>
                      <span className='fw-bold text-gray-900 fs-4'>
                        {Object.keys(tableMappings).length}
                      </span>
                    </div>
                  </div>
                </div>
                {file && (
                  <div className='col-md-4'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-45px me-4'>
                        <span className='symbol-label bg-light-info'>
                          <i className='fas fa-file-excel text-info fs-3'></i>
                        </span>
                      </div>
                      <div>
                        <span className='text-muted fs-7 d-block'>File Size</span>
                        <span className='fw-bold text-gray-900 fs-4'>
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </KTCardBody>
          </KTCard>
        </div>

        {/* Data Mapping Visualization */}
        {(() => {
          const mappedEntries = (Object.entries(columnMappings) as Array<[string, string]>).filter(
            ([, dbCol]) => dbCol && dbCol.trim() !== ''
          )
          if (mappedEntries.length === 0) return null

          return (
            <KTCard className='mb-4'>
              <KTCardBody className='py-5'>
                <div className='d-flex align-items-center mb-4'>
                  <i className='fas fa-project-diagram fs-2x text-primary me-3'></i>
                  <div>
                    <h3 className='mb-1'>
                      {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.DATA_MAPPING'})}
                    </h3>
                    <p className='text-muted mb-0 fs-7'>
                      {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.DATA_MAPPING_DESC'})}
                    </p>
                  </div>
                </div>

                <div className='separator separator-dashed my-5'></div>

                {/* Table header */}
                <div
                  className='row align-items-center g-0 mb-2 text-center'
                  style={{padding: '12px 0'}}
                >
                  <div className='col-5 pe-3 text-center'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{gap: 12}}
                    >
                      <span className='text-muted fs-5 fw-semibold'>
                        {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.YOUR_COLUMN'})}
                      </span>
                    </div>
                  </div>
                  <div className='col-2 px-3 text-center' />
                  <div className='col-5 ps-3 text-center'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{gap: 12}}
                    >
                      <span className='text-muted fs-5 fw-semibold'>
                        {intl.formatMessage({id: 'CSV_IMPORT.REVIEW.MAP_TO'})}
                      </span>
                    </div>
                  </div>
                </div>

                <div className='separator separator-dashed mb-0'></div>

                <div className='d-flex flex-column' style={{rowGap: 0}}>
                  {mappedEntries.map(([csvCol, dbCol]: [string, string], idx) => (
                    <React.Fragment key={`${csvCol}-${dbCol}-${idx}`}>
                      {idx > 0 && (
                        <div
                          className='separator separator-dashed'
                          style={{margin: 0, flexShrink: 0}}
                        />
                      )}
                      <div
                        className='row align-items-center g-0 text-center'
                        style={{padding: '16px 0', minHeight: 52}}
                      >
                        <div className='col-5 pe-3 text-center'>
                          <span className='fw-semibold text-gray-800 text-truncate d-block'>
                            {formatCsvColumnName(csvCol)}
                          </span>
                        </div>
                        <div className='col-2 px-3 text-center'>
                          <i className='fas fa-arrow-right text-primary fs-3'></i>
                        </div>
                        <div className='col-5 ps-3 text-center'>
                          <span className='fw-semibold text-gray-800 text-truncate d-block'>
                            {getDbColumnDisplayName(dbCol)}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </KTCardBody>
            </KTCard>
          )
        })()}
      </div>
    </div>
  )
}

export default CsvImportReview
